import React, { useEffect, useState } from "react";
import { FiCopy, FiServer, FiSettings, FiKey, FiGlobe, FiCpu, FiCheckCircle } from "react-icons/fi";
import { API_BASE_URL } from "../config";
import { toast } from "react-toastify";
import { Button, Card, CardBody, PageHeader, Badge, EmptyState } from "./ui";

const SECTIONS = [
    { id: "webhook", label: "Webhook", icon: FiServer },
    { id: "token", label: "Admin Token", icon: FiKey },
    { id: "general", label: "General", icon: FiSettings },
    { id: "system", label: "System", icon: FiCpu },
];

export default function SettingsPanel() {
    const [activeSection, setActiveSection] = useState("webhook");
    const [webhookInfo, setWebhookInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWebhookInfo();
        fetchTokenInfo();
    }, []);

    const fetchWebhookInfo = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/webhook-info`);
            const data = await res.json();
            if (data.success) {
                setWebhookInfo(prev => ({ ...prev, ...data }));
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError("Gagal mengambil data server: " + err.message);
        }
        setLoading(false);
    };

    const fetchTokenInfo = async () => {
        try {
            const module = await import("../supabaseClient");
            const supabase = module.supabase;

            const { data } = await supabase
                .from('admin_token')
                .select('*')
                .eq('id', 1)
                .single();

            if (data) {
                setWebhookInfo(prev => ({
                    ...prev,
                    admin_token: data.token,
                    token_expiry: data.expires_at
                }));
            }
        } catch (e) {
            console.error(e);
        }
    };

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`✅ ${label} berhasil disalin!`);
    };

    const generateToken = async () => {
        const token = Math.floor(100000 + Math.random() * 900000).toString();
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + 7);

        try {
            const module = await import("../supabaseClient");
            const supabase = module.supabase;

            const { error: upsertError } = await supabase
                .from('admin_token')
                .upsert({ id: 1, token: token, expires_at: expiry.toISOString() });

            if (upsertError) {
                toast.error("Gagal update token. Pastikan tabel admin_token ada.");
                console.error(upsertError);
            } else {
                toast.success("✅ Token baru berhasil digenerate!");
                setWebhookInfo(prev => ({ ...prev, admin_token: token, token_expiry: expiry.toISOString() }));
            }
        } catch (e) {
            toast.error("Error: " + e.message);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-9 w-48 rounded-lg bg-slate-200 dark:bg-slate-800" />
                <div className="h-64 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            <PageHeader title="Pengaturan" subtitle="Konfigurasi sistem dan informasi server" icon={FiSettings} />

            {error && (
                <div className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
                {/* Section nav */}
                <div className="lg:col-span-1">
                    <Card className="p-2">
                        <nav className="flex gap-1 overflow-x-auto scrollbar-slim lg:flex-col">
                            {SECTIONS.map((s) => {
                                const Icon = s.icon;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => setActiveSection(s.id)}
                                        className={`flex flex-shrink-0 items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors lg:w-full ${
                                            activeSection === s.id
                                                ? "bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300"
                                                : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                                        }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {s.label}
                                    </button>
                                );
                            })}
                        </nav>
                    </Card>
                </div>

                {/* Section content */}
                <div className="space-y-6 lg:col-span-3">
                    {activeSection === "webhook" && (
                        <Card>
                            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Webhook &amp; Payment Configuration</h2>
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                    Gunakan informasi ini untuk menghubungkan Saweria dengan Bot Anda.
                                </p>
                            </div>
                            <CardBody className="space-y-5">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Server Public IP (VPS)</label>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 truncate rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                            {webhookInfo?.ip || "-"}
                                        </code>
                                        <Button variant="secondary" size="icon" onClick={() => copyToClipboard(webhookInfo?.ip, "IP Address")} aria-label="Salin IP">
                                            <FiCopy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Webhook URL (Untuk Saweria)</label>
                                        {webhookInfo?.port && <Badge tone="success">Port: {webhookInfo.port}</Badge>}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <code className="flex-1 break-all rounded-xl border border-primary-100 bg-primary-50 p-3 font-mono text-sm text-primary-700 dark:border-primary-500/20 dark:bg-primary-500/10 dark:text-primary-300">
                                            {webhookInfo?.webhook_url || "-"}
                                        </code>
                                        <Button variant="secondary" size="icon" onClick={() => copyToClipboard(webhookInfo?.webhook_url, "Webhook URL")} aria-label="Salin URL">
                                            <FiCopy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                                        Masukkan URL ini ke kolom <strong>Webhook URL</strong> di pengaturan Saweria. Pastikan port {webhookInfo?.port} dibuka di firewall VPS.
                                    </p>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {activeSection === "token" && (
                        <Card>
                            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Token Akses Bot Admin</h2>
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                    Token ini digunakan untuk login ke Admin Bot Telegram. Berlaku selama 7 hari.
                                </p>
                            </div>
                            <CardBody className="space-y-5">
                                <Button icon={FiKey} onClick={generateToken}>
                                    Generate Token Baru
                                </Button>

                                {webhookInfo?.admin_token ? (
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-800/50">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <p className="text-xs text-slate-500 dark:text-slate-400">Token Aktif</p>
                                                <p className="mt-1 font-mono text-3xl font-bold tracking-wider text-slate-800 dark:text-slate-100">
                                                    {webhookInfo.admin_token}
                                                </p>
                                                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-danger-500">
                                                    <FiCheckCircle className="h-3.5 w-3.5" />
                                                    Expired: {new Date(webhookInfo.token_expiry).toLocaleDateString("id-ID")}
                                                </p>
                                            </div>
                                            <Button variant="ghost" size="icon" onClick={() => copyToClipboard(webhookInfo.admin_token, "Token Admin")} aria-label="Salin token">
                                                <FiCopy className="h-5 w-5" />
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <EmptyState icon={FiKey} title="Belum ada token aktif" description="Silakan generate token baru." />
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {activeSection === "general" && (
                        <Card>
                            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Pengaturan Umum</h2>
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Segera hadir</p>
                            </div>
                            <CardBody>
                                <EmptyState
                                    icon={FiSettings}
                                    title="Pengaturan Umum (Segera Hadir)"
                                    description="Pengaturan auto-reply dan template pesan akan tersedia di sini."
                                />
                            </CardBody>
                        </Card>
                    )}

                    {activeSection === "system" && (
                        <Card>
                            <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                                <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">System</h2>
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Informasi aplikasi</p>
                            </div>
                            <CardBody className="space-y-4">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">API Base URL</label>
                                    <code className="block rounded-xl border border-slate-200 bg-slate-50 p-3 font-mono text-sm text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                                        {API_BASE_URL}
                                    </code>
                                </div>
                                <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400">
                                            <FiGlobe className="h-5 w-5" />
                                        </span>
                                        <div>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">XF Admin Panel</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Versi 1.0</p>
                                        </div>
                                    </div>
                                    <Badge tone="success" dot>Aktif</Badge>
                                </div>
                            </CardBody>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
}
